import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { createOrder } from "../lib/api";
import Seo from "../components/Seo";
import { Lock, ShieldCheck, Clock, Trash2, Wrench, Truck } from "lucide-react";

const FITTING_FEE = 25;
const TIME_SLOTS = ["08:00 – 10:00", "10:00 – 12:00", "12:00 – 14:00", "14:00 – 16:00", "16:00 – 18:00", "18:00 – 20:00"];

const Checkout = () => {
  const { items, updateQuantity, removeItem, subtotal, clear, count } = useCart();
  const [serviceType, setServiceType] = useState("mobile_fitting"); // "mobile_fitting" | "delivery"
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", address: "", postcode: "", fitting_date: "", time_slot: "", payment_method: "card", same_billing: true });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isFitting = serviceType === "mobile_fitting";
  const fittingFee = isFitting && items.length ? FITTING_FEE : 0;
  const total = subtotal + fittingFee;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPlaceOrder = async () => {
    setError("");
    const baseMissing = !form.full_name || !form.phone || !form.address || !form.postcode;
    const fittingMissing = isFitting && (!form.fitting_date || !form.time_slot);
    if (baseMissing || fittingMissing) {
      setError(isFitting
        ? "Please complete your details, fitting date and time slot."
        : "Please complete your delivery details.");
      return;
    }
    try {
      setSubmitting(true);
      const order = await createOrder({
        items: items.map((i) => ({ tyre_id: i.tyre_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        subtotal,
        fitting_fee: fittingFee,
        total,
        service_type: serviceType,
        ...form,
        // Force blanks when delivery-only so we don't persist stale fitting fields
        fitting_date: isFitting ? form.fitting_date : "",
        time_slot: isFitting ? form.time_slot : "",
      });
      if (form.payment_method === "card") {
        const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/checkout/session`, {
          order_id: order.id,
          origin_url: window.location.origin,
        });
        clear();
        window.location.href = data.url;
        return;
      }
      clear();
      navigate(`/confirmation/${order.id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (count === 0) {
    return (
      <div className="vd-page" data-testid="page-checkout-empty">
        <Seo title="Secure Checkout" description="Complete your tyre or service order with our secure SSL checkout — fast UK delivery and free fitting included with any tyre purchase." path="/checkout" />
        <section className="vd-container py-20 text-center">
          <h1 className="mb-4">Your basket is empty</h1>
          <p className="vd-muted mb-8">Add tyres or services to begin checkout.</p>
          <Link to="/tyres" className="vd-btn-primary">Shop Tyres</Link>
        </section>
      </div>
    );
  }

  return (
    <div className="vd-page" data-testid="page-checkout">
      <Seo title="Secure Checkout" description="Complete your tyre or service order with our secure SSL checkout — fast UK delivery and free fitting included with any tyre purchase." path="/checkout" />
      <section className="vd-section-secondary py-12 relative overflow-hidden">
        <div className="vd-red-accent-line" style={{ top: "20%" }} aria-hidden="true" />
        <div className="vd-red-accent-line" style={{ bottom: "20%" }} aria-hidden="true" />
        <div className="vd-container text-center relative">
          <div className="flex items-center gap-4 my-2 max-w-3xl mx-auto">
            <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
            <h1 className="px-2 whitespace-nowrap" style={{ fontSize: 44 }}>Secure Checkout</h1>
            <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
          </div>
          <p className="vd-muted mt-3">Fast, safe, and simple — complete your order in seconds.</p>
        </div>
      </section>

      <section className="vd-container py-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="vd-card p-6" data-testid="order-summary-card">
          <h3 className="mb-5">Order Summary</h3>
          <div className="space-y-4">
            {items.map((i) => (
              <div key={i.tyre_id} className="flex items-center gap-4 pb-4 border-b" style={{ borderColor: "var(--border-color)" }} data-testid={`order-item-${i.tyre_id}`}>
                <img src={i.image} alt={i.name} className="w-16 h-16 rounded-md object-cover" />
                <div className="flex-1">
                  <p className="vd-heading font-semibold text-sm">{i.name}</p>
                  <p className="vd-muted text-xs">£{i.price.toFixed(2)} each</p>
                  <div className="inline-flex items-center mt-2 border rounded-md" style={{ borderColor: "var(--border-color)" }}>
                    <button onClick={() => updateQuantity(i.tyre_id, i.quantity - 1)} className="px-2 py-1 vd-heading" data-testid={`dec-${i.tyre_id}`}>−</button>
                    <span className="px-3 vd-heading font-semibold">{i.quantity}</span>
                    <button onClick={() => updateQuantity(i.tyre_id, i.quantity + 1)} className="px-2 py-1 vd-heading" data-testid={`inc-${i.tyre_id}`}>+</button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold" style={{ color: "var(--primary-red)" }}>£{(i.price * i.quantity).toFixed(2)}</p>
                  <button onClick={() => removeItem(i.tyre_id)} className="text-xs vd-muted hover:text-[var(--primary-red)] flex items-center gap-1 mt-1" data-testid={`remove-${i.tyre_id}`}><Trash2 size={12} /> Remove</button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-2">
            <Row k="Subtotal" v={`£${subtotal.toFixed(2)}`} />
            {isFitting ? (
              <Row k="Mobile Fitting Fee" v={`£${FITTING_FEE.toFixed(2)}`} testid="row-fitting-fee" />
            ) : (
              <Row k="Delivery" v="FREE" testid="row-delivery-free" />
            )}
            <div className="border-t my-2" style={{ borderColor: "var(--border-color)" }} />
            <div className="flex justify-between" data-testid="order-total">
              <span className="vd-heading font-bold text-lg">Total</span>
              <span className="font-extrabold text-lg" style={{ color: "var(--primary-red)" }}>£{total.toFixed(2)}</span>
            </div>
          </div>
          <button onClick={() => navigate("/tyres")} className="vd-btn-secondary w-full mt-5" data-testid="edit-basket">Edit Basket</button>
        </div>

        <div className="vd-card p-6" data-testid="checkout-form-card">
          {/* Service type toggle — choose between booked mobile fitting and delivery-only */}
          <h4 className="vd-heading font-semibold mb-3">How would you like to receive your tyres?</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="service-type-toggle">
            <ServiceOption
              testid="service-option-fitting"
              active={isFitting}
              onClick={() => setServiceType("mobile_fitting")}
              icon={<Wrench size={18} />}
              title="Mobile Fitting"
              sub={`We come to you · +£${FITTING_FEE.toFixed(0)}`}
            />
            <ServiceOption
              testid="service-option-delivery"
              active={!isFitting}
              onClick={() => setServiceType("delivery")}
              icon={<Truck size={18} />}
              title="Delivery Only"
              sub="Tyres shipped to you · FREE"
            />
          </div>

          <h3 className="mb-5">{isFitting ? "Delivery & Fitting Details" : "Delivery Details"}</h3>
          <div className="space-y-3">
            <input className="vd-input" placeholder="Full Name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="input-full-name" />
            <input className="vd-input" placeholder="Phone Number" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="input-phone" />
            <input type="email" className="vd-input" placeholder="Email (optional — for order updates)" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" />
            <input className="vd-input" placeholder={isFitting ? "Address" : "Shipping Address"} value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="input-address" />
            <input className="vd-input" placeholder="Postcode" value={form.postcode} onChange={(e) => update("postcode", e.target.value)} data-testid="input-postcode" />
            {isFitting && (
              <>
                <input type="date" className="vd-input" value={form.fitting_date} onChange={(e) => update("fitting_date", e.target.value)} data-testid="input-fitting-date" />
                <select className="vd-select" value={form.time_slot} onChange={(e) => update("time_slot", e.target.value)} data-testid="input-time-slot">
                  <option value="">Select Time Slot</option>
                  {TIME_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="vd-checkbox" checked={form.same_billing} onChange={(e) => update("same_billing", e.target.checked)} data-testid="input-same-billing" />
              <span className="vd-body text-sm">Use same address for billing</span>
            </label>
          </div>

          <h4 className="vd-heading font-semibold mt-7 mb-3">Payment Method</h4>
          <div className="space-y-2">
            {[
              { id: "card", label: "Credit / Debit Card" },
              { id: "paypal", label: "PayPal" },
              { id: "apple", label: "Apple Pay" },
            ].map((p) => (
              <label key={p.id} className="flex items-center gap-3 p-3 rounded-md border cursor-pointer" style={{ borderColor: form.payment_method === p.id ? "var(--primary-red)" : "var(--border-color)" }} data-testid={`payment-${p.id}`}>
                <input type="radio" name="payment" className="vd-checkbox" checked={form.payment_method === p.id} onChange={() => update("payment_method", p.id)} />
                <span className="vd-body">{p.label}</span>
              </label>
            ))}
          </div>

          {error && <p className="mt-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="checkout-error">{error}</p>}

          <button onClick={onPlaceOrder} disabled={submitting} className="vd-btn-primary w-full mt-6" data-testid="pay-now-btn">
            {submitting ? "Processing..." : `Pay Now £${total.toFixed(2)}`}
          </button>
          <Link to="/tyres" className="vd-btn-secondary w-full mt-3" data-testid="back-to-basket">Back to Shopping</Link>

          <div className="flex flex-wrap items-center justify-center gap-5 mt-6 text-sm vd-muted" data-testid="trust-badges">
            <span className="flex items-center gap-1.5"><Lock size={14} /> Secure SSL</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} /> Trusted Payments</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> Fast Processing</span>
          </div>
          <p className="vd-muted text-center text-xs mt-3">Your payment is encrypted and protected.</p>
        </div>
      </section>

      <section className="vd-container py-10 relative">
        <div className="vd-red-accent-line" style={{ bottom: 0 }} aria-hidden="true" />
        <div className="text-center">
          <p className="vd-heading font-semibold mb-5">Need help with your order?</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:07979210250" className="vd-btn-primary" data-testid="checkout-call-us">Call Us</a>
            <a href="https://wa.me/447979210250" target="_blank" rel="noopener noreferrer" className="vd-btn-secondary" data-testid="checkout-chat-support">Chat with Support</a>
          </div>
        </div>
      </section>
    </div>
  );
};

const Row = ({ k, v, testid }) => (
  <div className="flex justify-between vd-body" data-testid={testid}><span>{k}</span><span>{v}</span></div>
);

const ServiceOption = ({ active, onClick, icon, title, sub, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left rounded-md p-4 transition-all"
    style={{
      border: `2px solid ${active ? "var(--primary-red)" : "var(--border-color)"}`,
      background: active ? "rgba(230,0,0,0.05)" : "transparent",
    }}
    data-testid={testid}
    aria-pressed={active}
  >
    <span className="inline-flex items-center justify-center rounded-full mb-2" style={{ width: 32, height: 32, background: active ? "var(--primary-red)" : "var(--bg-secondary)", color: active ? "#fff" : "var(--text-heading)" }}>
      {icon}
    </span>
    <div className="vd-heading font-semibold">{title}</div>
    <div className="vd-muted text-xs mt-0.5">{sub}</div>
  </button>
);

export default Checkout;
